import { createSelector } from "@reduxjs/toolkit";
import { ACTIVITY_TYPES, ACTIVITY_CONTEXT } from "Constants/constants";
import { RootState } from "Store/configureStore";

import type { Activity } from "Api/activity/types";

// A stable reference for "no scheduled activities fetched yet" so the two
// createSelector calls below can tell "nothing changed" from "new data
// arrived": `?? {}` would hand back a fresh object every call, which always
// counts as a changed input and defeats the memoization (recomputing —
// and returning a new array reference from — the output selector on every
// single call, even before anything has actually loaded).
const EMPTY_ACTIVITIES_MAP: Record<string, Activity> = {};

export type ActivityParams = {
  activityId: string;
  organizationId: string;
  projectId: string;
};

export type ProjectParams = {
  organizationId: string;
  projectId: string;
};

export type IntegrationParams = {
  organizationId: string;
  projectId: string;
  integrationId: string;
};

export type EnvironmentParams = {
  organizationId: string;
  projectId: string;
  environmentId: string;
};

type Status =
  "in_progress" | "pending" | "complete" | "cancelled" | "scheduled" | "staged";

type Result = "failure" | "success";

const sortByCreationDate = (
  item1: { created_at: string },
  item2: { created_at: string }
) =>
  new Date(item2.created_at).getTime() - new Date(item1.created_at).getTime();

export const selectAllProjectActivities = (
  state: RootState,
  { organizationId, projectId }: ProjectParams
) => {
  return Object.fromEntries(
    Object.entries(
      state.activity?.byProject?.data?.[organizationId]?.[projectId] ?? {}
    ).sort(([, value1], [, value2]) => sortByCreationDate(value1, value2))
  );
};

const selectProjectActivitiesByStatus =
  (status: Status | Status[]) => (state: RootState, params: ProjectParams) => {
    const allActivities = selectAllProjectActivities(state, params);
    return Object.values(allActivities)
      .filter(activity =>
        typeof status === "string"
          ? activity.state === status
          : (status as string[]).includes(activity.state)
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

const selectProjectActivitiesByResult =
  (result: Result | Result[]) => (state: RootState, params: ProjectParams) => {
    const allActivities = selectAllProjectActivities(state, params);
    return Object.values(allActivities)
      .filter(activity =>
        typeof result === "string"
          ? activity.result === result
          : activity.result !== null &&
            (result as string[]).includes(activity.result)
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

export const selectInProgressProjectActivities =
  selectProjectActivitiesByStatus("in_progress");

const selectStagedProjectActivities = selectProjectActivitiesByStatus("staged");

const selectPendingProjectActivities = selectProjectActivitiesByStatus([
  "pending",
  "scheduled"
]);

export const selectPendingScheduledProjectActivities = createSelector(
  [
    (state: RootState, { organizationId, projectId }: ProjectParams) =>
      state.activity?.pendingScheduled?.byProject?.data?.[organizationId]?.[
        projectId
      ] ?? EMPTY_ACTIVITIES_MAP
  ],
  activities => Object.values(activities).sort(sortByCreationDate)
);

const selectCompletedProjectActivities = selectProjectActivitiesByStatus([
  "complete",
  "cancelled"
]);

const selectFailedProjectActivities =
  selectProjectActivitiesByResult("failure");

const selectProjectHasMoreActivities = (state: RootState) =>
  state.activity?.byProject?.hasMore;

// Deep-link consumers (e.g. LogModal) need only the paginated feed's own
// loading state, not the separate pendingScheduled fetch's — see
// selectPendingScheduledProjectActivitiesLoadingState for that one.
export const selectProjectActivityLoadingState = (state: RootState) =>
  Boolean(state.activity?.byProject?.loading);

export const selectPendingScheduledProjectActivitiesLoadingState = (
  state: RootState
) => Boolean(state.activity?.pendingScheduled?.byProject?.loading);

const selectProjectActivityLoadingMoreState = (state: RootState) =>
  state.activity?.byProject?.loadingMore;

export const selectProjectActivitiesByType =
  (types: Array<string>) => (state: RootState, params: ProjectParams) => {
    const allActivities = selectAllProjectActivities(state, params);
    return Object.values(allActivities)
      .filter(activity => types.includes(activity.type))
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

export const selectProjectActivityById = (
  state: RootState,
  params: ActivityParams
) => {
  const allActivities = selectAllProjectActivities(state, params);
  return allActivities?.[params.activityId];
};

export const selectAllEnvironmentActivities = createSelector(
  [
    (
      state: RootState,
      { organizationId, projectId, environmentId }: EnvironmentParams
    ) =>
      state.activity?.byEnvironment?.data?.[organizationId]?.[projectId]?.[
        environmentId
      ] ?? {}
  ],
  activities => {
    return Object.fromEntries(
      Object.entries(activities).sort(([, value1], [, value2]) =>
        sortByCreationDate(value1, value2)
      )
    );
  }
);

export const getActivityFilters = (state: RootState, context: string) =>
  state.activity?.filters?.[context];

const selectEnvironmentActivitiesByStatus =
  (status: Status | Status[]) =>
  (state: RootState, params: EnvironmentParams) => {
    const activities = selectAllEnvironmentActivities(state, params);
    return Object.values(activities)
      .filter(activity => {
        return typeof status === "string"
          ? activity.state === status
          : (status as string[]).includes(activity.state);
      })
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

const selectEnvironmentActivitiesByResult =
  (result: Result | Result[]) =>
  (state: RootState, params: EnvironmentParams) => {
    const activities = selectAllEnvironmentActivities(state, params);
    return Object.values(activities)
      .filter(activity =>
        typeof result === "string"
          ? activity.result === result
          : activity.result !== null &&
            (result as string[]).includes(activity.result)
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

const selectInProgressEnvironmentActivities =
  selectEnvironmentActivitiesByStatus("in_progress");
const selectStagedEnvironmentActivities =
  selectEnvironmentActivitiesByStatus("staged");

export const selectPendingEnvironmentActivities = createSelector(
  [selectAllEnvironmentActivities],
  allActivities => {
    return Object.values(allActivities)
      .filter(
        activity =>
          activity.state === "pending" || activity.state === "scheduled"
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  }
);

export const selectPendingScheduledEnvironmentActivities = createSelector(
  [
    (
      state: RootState,
      { organizationId, projectId, environmentId }: EnvironmentParams
    ) =>
      state.activity?.pendingScheduled?.byEnvironment?.data?.[organizationId]?.[
        projectId
      ]?.[environmentId] ?? EMPTY_ACTIVITIES_MAP
  ],
  activities => Object.values(activities).sort(sortByCreationDate)
);

const selectCompletedEnvironmentActivities =
  selectEnvironmentActivitiesByStatus(["complete", "cancelled"]);

const selectFailedEnvironmentActivities =
  selectEnvironmentActivitiesByResult("failure");

const selectEnvironmentActivityLoadingState = (state: RootState) =>
  Boolean(state.activity?.byEnvironment?.loading);

const selectPendingScheduledEnvironmentActivitiesLoadingState = (
  state: RootState
) => Boolean(state.activity?.pendingScheduled?.byEnvironment?.loading);

const selectEnvironmentActivityLoadingMoreState = (state: RootState) =>
  state.activity?.byEnvironment?.loadingMore;

const selectEnvironmentHasMoreActivities = (state: RootState) =>
  state.activity?.byEnvironment?.hasMore;

const selectAllIntegrationActivities = (
  state: RootState,
  { organizationId, projectId, integrationId }: IntegrationParams
) => {
  return Object.fromEntries(
    Object.entries(
      state.activity?.byIntegration?.data?.[organizationId]?.[projectId]?.[
        integrationId
      ] ?? {}
    ).sort(([, value1], [, value2]) => sortByCreationDate(value1, value2))
  );
};

const selectIntegrationActivitiesByStatus =
  (status: Status | Status[]) =>
  (state: RootState, params: IntegrationParams) => {
    const activities = selectAllIntegrationActivities(state, params);
    return Object.values(activities)
      .filter(activity =>
        typeof status === "string"
          ? activity.state === status
          : status.includes(activity.state as Status)
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

const selectIntegrationActivitiesByResult =
  (result: Result | Result[]) =>
  (state: RootState, params: IntegrationParams) => {
    const activities = selectAllIntegrationActivities(state, params);
    return Object.values(activities)
      .filter(activity =>
        typeof result === "string"
          ? activity.result === result
          : result.includes(activity.result as Result)
      )
      .sort((activity1, activity2) => sortByCreationDate(activity1, activity2));
  };

export const getActivityTab = (state: RootState) =>
  state.activity?.filters?.tab || "recent";

const selectInProgressIntegrationActivities =
  selectIntegrationActivitiesByStatus("in_progress");
const selectStagedIntegrationActivities =
  selectIntegrationActivitiesByStatus("staged");

const selectPendingIntegrationActivities = selectIntegrationActivitiesByStatus([
  "pending",
  "scheduled"
]);

const selectCompletedIntegrationActivities =
  selectIntegrationActivitiesByStatus(["complete", "cancelled"]);

const selectFailedIntegrationActivities =
  selectIntegrationActivitiesByResult("failure");

const selectIntegrationActivityLoadingState = (state: RootState) =>
  state.activity?.byIntegration?.loading;

const selectIntegrationActivityLoadingMoreState = (state: RootState) =>
  state.activity?.byIntegration?.loadingMore;

const selectIntegrationHasMoreActivities = (state: RootState) =>
  state.activity?.byIntegration?.hasMore;

export type Context =
  | "environment"
  | "integration"
  | `integration:${string}`
  | `integration.${string}`
  | "project"
  | "crons";

export const getSelectors = (context: Context, params: any) => {
  const selectors = {
    environment: {
      all: (state: RootState) => selectAllEnvironmentActivities(state, params),
      inProgress: (state: RootState) =>
        selectInProgressEnvironmentActivities(state, params),
      pending: (state: RootState) =>
        selectPendingEnvironmentActivities(state, params),
      staged: (state: RootState) =>
        selectStagedEnvironmentActivities(state, params),
      completed: (state: RootState) =>
        selectCompletedEnvironmentActivities(state, params),
      failed: (state: RootState) =>
        selectFailedEnvironmentActivities(state, params),
      isLoading: (state: RootState) =>
        selectEnvironmentActivityLoadingState(state) ||
        selectPendingScheduledEnvironmentActivitiesLoadingState(state),
      isLoadingMore: (state: RootState) =>
        selectEnvironmentActivityLoadingMoreState(state),
      hasMore: (state: RootState) => selectEnvironmentHasMoreActivities(state)
    },
    integration: {
      all: (state: RootState) => selectAllIntegrationActivities(state, params),
      inProgress: (state: RootState) =>
        selectInProgressIntegrationActivities(state, params),
      pending: (state: RootState) =>
        selectPendingIntegrationActivities(state, params),
      staged: (state: RootState) =>
        selectStagedIntegrationActivities(state, params),
      completed: (state: RootState) =>
        selectCompletedIntegrationActivities(state, params),
      failed: (state: RootState) =>
        selectFailedIntegrationActivities(state, params),
      isLoading: (state: RootState) =>
        selectIntegrationActivityLoadingState(state),
      isLoadingMore: (state: RootState) =>
        selectIntegrationActivityLoadingMoreState(state),
      hasMore: (state: RootState) => selectIntegrationHasMoreActivities(state)
    },
    project: {
      all: (state: RootState) => selectAllProjectActivities(state, params),
      inProgress: (state: RootState) =>
        selectInProgressProjectActivities(state, params),
      pending: (state: RootState) =>
        selectPendingProjectActivities(state, params),
      staged: (state: RootState) =>
        selectStagedProjectActivities(state, params),
      completed: (state: RootState) =>
        selectCompletedProjectActivities(state, params),
      failed: (state: RootState) =>
        selectFailedProjectActivities(state, params),
      isLoading: (state: RootState) =>
        selectProjectActivityLoadingState(state) ||
        selectPendingScheduledProjectActivitiesLoadingState(state),
      isLoadingMore: (state: RootState) =>
        selectProjectActivityLoadingMoreState(state),
      hasMore: (state: RootState) => selectProjectHasMoreActivities(state)
    }
  };

  // context integration like 'integration.gitlab'
  const kind = (
    context.startsWith("integration") ? "integration" : context
  ) as keyof typeof selectors;

  // Use environment selectors for crons context
  if (context === ACTIVITY_CONTEXT.Crons) {
    return selectors["environment"];
  }

  return selectors[kind] || selectors["project"];
};

export const selectBackupActivities = (
  state: RootState,
  params: EnvironmentParams
) => {
  const environmentActivities = selectAllEnvironmentActivities(state, params);
  return Object.values(environmentActivities)
    .filter(elt =>
      [
        ACTIVITY_TYPES.ENVIRONMENT.BACKUP as string,
        ACTIVITY_TYPES.ENVIRONMENT.RESTORE as string,
        ACTIVITY_TYPES.ENVIRONMENT.BACKUP_DELETE as string
      ].includes(elt.type)
    )
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
};

export const selectDeployInProgressActivity = (
  state: RootState,
  params: EnvironmentParams
) => {
  const environmentInProgressActivities = selectInProgressEnvironmentActivities(
    state,
    params
  );
  return Object.values(environmentInProgressActivities).filter(
    elt => elt.type === ACTIVITY_TYPES.ENVIRONMENT.DEPLOY
  );
};
export const selectRedeployInProgressActivity = (
  state: RootState,
  params: EnvironmentParams
) => {
  const environmentInProgressActivities = selectInProgressEnvironmentActivities(
    state,
    params
  );
  return Object.values(environmentInProgressActivities).filter(
    elt => elt.type === ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY
  );
};
export const selectEnvironmentStagedActivities = (
  state: RootState,
  { organizationId, projectId, environmentId }: EnvironmentParams
) => {
  return Object.values(
    state.activity?.staged?.data?.[organizationId]?.[projectId]?.[
      environmentId
    ] ?? {}
  );
};

export const selectIsLastEnvironmentActivityFailed = (
  state: RootState,
  params: EnvironmentParams
) => {
  const completedActivities = selectCompletedEnvironmentActivities(
    state,
    params
  );
  if (!completedActivities.length) return false;

  // completedActivities is already sorted by date (newest first) from selectCompletedEnvironmentActivities
  const lastCompletedActivity = completedActivities[0];
  return (
    lastCompletedActivity?.result === "failure" &&
    lastCompletedActivity?.state !== "cancelled"
  );
};
export const selectIsLastDeploymentActivityFailed = (
  state: RootState,
  params: EnvironmentParams
) => {
  const completedActivities = selectCompletedEnvironmentActivities(
    state,
    params
  );

  const deploymentActivities = completedActivities.filter(
    activity =>
      activity.type === ACTIVITY_TYPES.ENVIRONMENT.DEPLOY ||
      activity.type === ACTIVITY_TYPES.ENVIRONMENT.REDEPLOY
  );

  if (!deploymentActivities.length) return false;
  // completedActivities is already sorted by date (newest first) from selectCompletedEnvironmentActivities
  const lastDeployment = deploymentActivities[0];
  return (
    lastDeployment?.result === "failure" &&
    lastDeployment?.state !== "cancelled"
  );
};
export const selectInProgressProjectActivitiesSubscribed = createSelector(
  [selectAllProjectActivities],
  allActivities => {
    return Object.values(allActivities).filter(
      activity => activity.state === "in_progress"
    );
  }
);

export const selectInProgressEnvironmentActivitiesSubscribed = createSelector(
  [selectAllEnvironmentActivities],
  allEnvironmentActivities => {
    return Object.values(allEnvironmentActivities).filter(
      activity => activity.state === "in_progress"
    );
  }
);

export const selectInProgressIntegrationActivitiesSubscribed = createSelector(
  [selectAllIntegrationActivities],
  allIntegrationActivities => {
    return Object.values(allIntegrationActivities).filter(
      activity => activity.state === "in_progress"
    );
  }
);
